import React from "react";
import { observer } from "mobx-react";
import { TenantUpdateStore } from "../../store";
import { TenantUpdateForm } from "./TenantUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { EsignatureProvider } from "../../../../api/graphql";

export type TenantUpdateProps = {
  tenantUpdateStore: TenantUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const TenantUpdate: React.FC<TenantUpdateProps> = observer(
  ({ tenantUpdateStore, showToolbox = true, showSaveFooter }) => {
    return (
      <>
        <SavingOverlay open={tenantUpdateStore.isSaving} />
        <OutdatedAfterSaveNotice
          open={tenantUpdateStore.savedButOutdated}
          onReload={() => tenantUpdateStore.reloadTenant()}
          reloadDisabled={tenantUpdateStore.isProcessing}
        />
        <TenantUpdateForm
          tenantUpdateStore={tenantUpdateStore}
          nameError={tenantUpdateStore.errors.name?.message}
          onNameChange={(value: string) => {
            tenantUpdateStore.setNameValue(value);
          }}
          name={tenantUpdateStore.entityValues.name}
          tenantTypeError={tenantUpdateStore.errors.tenantType?.message}
          onTenantTypeChange={(value) => {
            tenantUpdateStore.setTenantTypeValue(value);
          }}
          tenantTypeSelectedValue={
            tenantUpdateStore.entityValues.showTenantType
          }
          onTenantTypeSelected={(value) => {
            tenantUpdateStore.setSelectedTenantType(value);
          }}
          tenantTypeOptions={tenantUpdateStore.getTenantTypeOptions()}
          statusError={tenantUpdateStore.errors.status?.message}
          onStatusChange={(value) => {
            tenantUpdateStore.setStatusValue(value);
          }}
          statusSelectedValue={tenantUpdateStore.entityValues.showStatus}
          onStatusSelected={(value) => {
            tenantUpdateStore.setSelectedStatus(value);
          }}
          statusOptions={tenantUpdateStore.getStatusOptions()}
          planError={tenantUpdateStore.errors.plan?.message}
          onPlanChange={(value) => {
            tenantUpdateStore.setPlanValue(value);
          }}
          planSelectedValue={tenantUpdateStore.entityValues.showPlan}
          onPlanSelected={(value) => {
            tenantUpdateStore.setSelectedPlan(value);
          }}
          planOptions={tenantUpdateStore.getPlanOptions()}
          logoOnRestoreFile={(id: string) =>
            tenantUpdateStore.logoRestoreFile(id)
          }
          logoOnLoadFile={(file: any) => tenantUpdateStore.logoUploadFile(file)}
          logoOnCommitFile={(changes) =>
            tenantUpdateStore.logoCommitFile(changes)
          }
          logoFiles={tenantUpdateStore.entityValues.__logo_files}
          logoError={tenantUpdateStore.errors.logo?.message}
          publicUrlError={tenantUpdateStore.errors.publicUrl?.message}
          onPublicUrlChange={(value: string) => {
            tenantUpdateStore.setPublicUrlValue(value);
          }}
          publicUrl={tenantUpdateStore.entityValues.publicUrl}
          idERPError={tenantUpdateStore.errors.idERP?.message}
          onIdERPChange={(value: string) => {
            tenantUpdateStore.setIdERPValue(value);
          }}
          idERP={tenantUpdateStore.entityValues.idERP}
          idCRMError={tenantUpdateStore.errors.idCRM?.message}
          onIdCRMChange={(value: string) => {
            tenantUpdateStore.setIdCRMValue(value);
          }}
          idCRM={tenantUpdateStore.entityValues.idCRM}
          monthlyTokenLimitError={
            tenantUpdateStore.errors.monthlyTokenLimit?.message
          }
          onMonthlyTokenLimitChange={(value: number | undefined) => {
            tenantUpdateStore.setMonthlyTokenLimitValue(value);
          }}
          monthlyTokenLimit={tenantUpdateStore.entityValues.monthlyTokenLimit}
          currentMonthTokensError={
            tenantUpdateStore.errors.currentMonthTokens?.message
          }
          onCurrentMonthTokensChange={(value: number) => {
            tenantUpdateStore.setCurrentMonthTokensValue(value);
          }}
          currentMonthTokens={tenantUpdateStore.entityValues.currentMonthTokens}
          tokenLimitResetDayError={
            tenantUpdateStore.errors.tokenLimitResetDay?.message
          }
          onTokenLimitResetDayChange={(value: number) => {
            tenantUpdateStore.setTokenLimitResetDayValue(value);
          }}
          tokenLimitResetDay={tenantUpdateStore.entityValues.tokenLimitResetDay}
          lastTokenResetDateError={
            tenantUpdateStore.errors.lastTokenResetDate?.message
          }
          onLastTokenResetDateChange={(value) => {
            tenantUpdateStore.setLastTokenResetDateValue(value);
          }}
          lastTokenResetDate={tenantUpdateStore.entityValues.lastTokenResetDate}
          ragLlmProviderError={tenantUpdateStore.errors.ragLlmProvider?.message}
          onRagLlmProviderChange={(value) => {
            tenantUpdateStore.setRagLlmProviderValue(value);
          }}
          ragLlmProviderSelectedValue={
            tenantUpdateStore.entityValues.showRagLlmProvider
          }
          onRagLlmProviderSelected={(value) => {
            tenantUpdateStore.setSelectedRagLlmProvider(value);
          }}
          ragLlmProviderOptions={tenantUpdateStore.getRagLlmProviderOptions()}
          ragLlmModelError={tenantUpdateStore.errors.ragLlmModel?.message}
          onRagLlmModelChange={(value: string) => {
            tenantUpdateStore.setRagLlmModelValue(value);
          }}
          ragLlmModel={tenantUpdateStore.entityValues.ragLlmModel}
          currentStorageBytesError={
            tenantUpdateStore.errors.currentStorageBytes?.message
          }
          onCurrentStorageBytesChange={(value: number) => {
            tenantUpdateStore.setCurrentStorageBytesValue(value);
          }}
          currentStorageBytes={
            tenantUpdateStore.entityValues.currentStorageBytes
          }
          storageByteLimitError={
            tenantUpdateStore.errors.storageByteLimit?.message
          }
          onStorageByteLimitChange={(value: number | undefined) => {
            tenantUpdateStore.setStorageByteLimitValue(value);
          }}
          storageByteLimit={tenantUpdateStore.entityValues.storageByteLimit}
          eSignatureProviderError={
            tenantUpdateStore.errors.eSignatureProvider?.message
          }
          eSignatureProviderOptions={tenantUpdateStore.getESignatureProviderOptions()}
          onESignatureProviderChange={(value: EsignatureProvider | "") => {
            tenantUpdateStore.setESignatureProviderValue(value);
          }}
          eSignatureProvider={tenantUpdateStore.entityValues.eSignatureProvider}
          dummyCRUDRestoredObservable={
            tenantUpdateStore.dummyCRUDRestoredObservable
          }
          showToolbox={showToolbox}
          showSaveFooter={showSaveFooter}
          saving={tenantUpdateStore.saveDisabled}
          onSave={() => tenantUpdateStore.saveTenant()}
          onCancel={() => tenantUpdateStore.cancelTenant()}
        />
        <ErrorDialog
          open={tenantUpdateStore.errorDialogOpened}
          title={tenantUpdateStore.errorTitle}
          description={tenantUpdateStore.errorDescription}
          onClose={() => tenantUpdateStore.closeErrorDialog()}
        />
        <ConfirmCancelDialog
          open={tenantUpdateStore.confirmDialogOpened}
          title={tenantUpdateStore.confirmDialogTitle}
          description={tenantUpdateStore.confirmDialogDescription}
          onConfirm={() => tenantUpdateStore.executeConfirmedAction()}
          onCancel={() => tenantUpdateStore.closeConfirmDialog()}
        />
      </>
    );
  }
);
